import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtom } from 'jotai';
import { searchValuesAtom, openSnackbarAdviseAtom } from '../../Atoms';
import useAPI from '../../api';
import { download } from '../../api/utils';
import SectionPage from '../../Components/Paper/SectionPage';
import mapFilters from '../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import ErrorDialog from '../../Components/Error';
import ConfirmPopup from '../../Components/Popup/ConfirmPopup';
import PurchaseListItem from './Components/PurchaseListItem';
import PurchaseFilters from './Components/PurchaseFilters';
import PurchaseDialog from './Components/PurchaseDialog';
import { PURCHASE_SORTING } from './Utils/constants';

const getPurchaseListItem = (
  data,
  onClick,
  onDelete,
  onSendPurchaseRequest,
  onAcceptPurchaseRequest,
  userInfo
) => (
  <PurchaseListItem
    key={data.id}
    data={data}
    onClick={onClick}
    onDelete={onDelete}
    onSendPurchaseRequest={onSendPurchaseRequest}
    onAcceptPurchaseRequest={onAcceptPurchaseRequest}
    userInfo={userInfo}
  />
);
const PurchaseHistoryPage = function PurchaseHistoryPage(userInfo) {
  const API = useAPI();
  const intl = useIntl();

  const [purchaseDialog, setPurchaseDialog] = useState(null);
  const [confirmClosing, setConfirmClosing] = useState(null);
  const [unitPrice, setUnitPrice] = useState(0);
  const [error, setError] = useState(null);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const openNewPurchaseDialog = () => {
    setPurchaseDialog(true);
  };
  const closeNewPurchaseDialog = () => {
    setPurchaseDialog(null);
  };

  const openEditPurchaseDialog = (purchaseData) => {
    setPurchaseDialog(purchaseData);
  };

  const closeDeleteNewPurchasePopup = () => {
    setConfirmClosing(true);
  };

  useEffect(() => {
    API.getUnitPrice((smsUnitPrice) => {
      setUnitPrice(smsUnitPrice);
    });
  }, []);

  const downloadAllPurchaseFile = useCallback(
    () =>
      download(
        'SMSPurchases',
        API.export2ExcelSMSPurchases({
          pageNumber: 0,
          filterExpression: mapFilters(
            ['Id'],
            [{ keyName: 'Id' }],
            [],
            searchValues
          ),
          sortExpression: [
            {
              sortValue: searchValues?.sortValue.value,
              sort: searchValues?.sortValue.direction,
            },
          ],
          silent: false,
        })
      ),
    [searchValues, error]
  );

  const getPurchaseHistory = useCallback(
    (pageNumber) =>
      API.getPurchaseHistory(
        {
          pageNumber,
          silent: pageNumber > 1,
          filterExpression: mapFilters(
            ['userFullName'],
            [{ keyName: 'state' }],
            [],
            searchValues
          ),
          sortExpression: [
            {
              sortValue: searchValues?.sortValue?.value,
              sort: searchValues?.sortValue?.direction,
            },
          ],
        },
        () => {
          saveFiltersInLocal(searchValues);
        },
        (err) => {
          setError(err);
        }
      ),

    [searchValues]
  );

  const {
    ObserverWrapper,
    setNewItems,
    recordCount,
    updateItem,
    removeClickedItem,
  } = useInfiniteQuery(getPurchaseHistory);

  const handleSubmit = (data) => {
    if (data.id === 'new-purchase') {
      // eslint-disable-next-line no-unused-vars
      const { id, ...rest } = data;
      API.createSMSPurchase(
        rest,
        (newPurchaseData) => {
          setNewItems(newPurchaseData, recordCount + 1);
          setPurchaseDialog(null);
          setOpenSnackbarAdvise({
            message: intl.messages.NEW_PURCHASE_CREATE_SUCCESSFULLY,
            severity: 'success',
          });
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.CREATE_PURCHASE_ERROR}`,
            severity: 'error',
          });
        }
      );
    } else {
      API.editSMSPurchase(
        data,
        (response) => {
          updateItem(response);
          setPurchaseDialog(null);
          setOpenSnackbarAdvise({
            message: intl.messages.PURCHASE_EDITED_SUCCESSFULLY,
            severity: 'success',
          });
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.EDITING_PURCHASE_ERROR}`,
            severity: 'error',
          });
        }
      );
    }
  };

  const sendPurchaseRequest = (id) => {
    API.updateSMSPurchaseStateToWaiting(
      id,
      (response) => {
        updateItem(response);
        setOpenSnackbarAdvise({
          message: intl.messages.NEW_PURCHASE_SEND_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.SEND_NEW_PURCHASE_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const acceptPurchaseRequest = (id) => {
    API.updateSMSPurchaseStateToProcessing(
      id,
      (response) => {
        updateItem(response);
        setOpenSnackbarAdvise({
          message: intl.messages.NEW_PURCHASE_ACCEPT_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.ACCEPT_NEW_PURCHASE_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const handleDeletePurchase = (id) => {
    API.deleteSMSPurchase(
      id,
      () => {
        removeClickedItem(id, recordCount - 1);
        setOpenSnackbarAdvise({
          message: intl.messages.PURCHASE_DELETED,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.PURCHASE_DELETE_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleChangesStatusFilters = (newPurchaseState) => {
    setSearchValues((prev) => ({
      ...prev,
      ...{ state: newPurchaseState },
    }));
  };
  const { userRoles } = userInfo;
  const isAdminOrSuperVisor = userRoles.find(
    (role) =>
      role.roleId === 'ADMIN' ||
      role.roleId === 'SUPERVISOR' ||
      role.roleId === 'SUPERADMIN'
  );
  return (
    <>
      <SectionPage
        sectionName="PURCHASE_HISTORY_CREDITS_SMS"
        sectionSubName="CREDITS"
        fab={isAdminOrSuperVisor ? intl.messages.NEW_PURCHASE : null}
        onClickFab={isAdminOrSuperVisor ? openNewPurchaseDialog : null}
        fabVariant="extended"
        ListObserverWrapper={ObserverWrapper}
        customEntryFunction={(item) =>
          getPurchaseListItem(
            item,
            openEditPurchaseDialog,
            handleDeletePurchase,
            sendPurchaseRequest,
            acceptPurchaseRequest,
            userInfo
          )
        }
        customFilters={
          <PurchaseFilters
            defaultValue={
              localStorage.getItem('state')
                ? null
                : JSON.parse(localStorage.getItem('state'))
            }
            onChangeFilters={handleChangesStatusFilters}
            userInfo={userInfo}
            onClose
          />
        }
        sortingOptions={PURCHASE_SORTING}
        filters={[{ keyName: 'state' }]}
        ExportToExcel={downloadAllPurchaseFile}
      />
      <PurchaseDialog
        id="new-purchase"
        title={intl.messages.NEW_PURCHASE}
        open={Boolean(purchaseDialog)}
        purchaseData={purchaseDialog}
        handleClose={closeNewPurchaseDialog}
        onSubmit={handleSubmit}
        unitPrice={unitPrice}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={closeDeleteNewPurchasePopup}
        handleConfirm={() => setPurchaseDialog(null)}
      />
      <ErrorDialog error={error} handleRetry={() => setError(null)} />
    </>
  );
};

export default PurchaseHistoryPage;
